import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { translations } from "../lib/i18n";

const SettingsContext = createContext(null);

export const SettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const [dyslexiaMode, setDyslexiaMode] = useState(
    () => localStorage.getItem("dyslexia_mode") === "true"
  );
  const [language, setLanguage] = useState(
    () => localStorage.getItem("preferred_language") || "en"
  );
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(
    () => localStorage.getItem("has_seen_onboarding") === "true"
  );
  const [loaded, setLoaded] = useState(false);

  // Sync from backend once user is loaded
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setLoaded(false);
      return;
    }
    (async () => {
      try {
        const s = await api.getSettings();
        if (cancelled) return;
        setDyslexiaMode(!!s.dyslexia_mode);
        setLanguage(s.preferred_language || "en");
        setHasSeenOnboarding(!!s.has_seen_onboarding);
        localStorage.setItem("dyslexia_mode", String(!!s.dyslexia_mode));
        localStorage.setItem("preferred_language", s.preferred_language || "en");
        localStorage.setItem("has_seen_onboarding", String(!!s.has_seen_onboarding));
      } catch (e) {
        // silent — fallback to local values
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Apply dyslexia body class (and html class for stricter coverage)
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    if (dyslexiaMode) {
      body.classList.add("dyslexia-mode");
      html.classList.add("dyslexia-mode");
    } else {
      body.classList.remove("dyslexia-mode");
      html.classList.remove("dyslexia-mode");
    }
    return () => {
      // leave as-is on unmount
    };
  }, [dyslexiaMode, loaded]);

  const updateDyslexia = useCallback(async (value) => {
    setDyslexiaMode(value);
    localStorage.setItem("dyslexia_mode", String(value));
    if (user) {
      try {
        await api.updateSettings({ dyslexia_mode: value });
      } catch (e) {
        // ignore
      }
    }
  }, [user]);

  const updateLanguage = useCallback(async (value) => {
    setLanguage(value);
    localStorage.setItem("preferred_language", value);
    if (user) {
      try {
        await api.updateSettings({ preferred_language: value });
      } catch (e) {
        // ignore
      }
    }
  }, [user]);

  const markOnboardingSeen = useCallback(async () => {
    setHasSeenOnboarding(true);
    localStorage.setItem("has_seen_onboarding", "true");
    if (user) {
      try {
        await api.updateSettings({ has_seen_onboarding: true });
      } catch (e) {
        // ignore
      }
    }
  }, [user]);

  const resetOnboarding = useCallback(async () => {
    setHasSeenOnboarding(false);
    localStorage.setItem("has_seen_onboarding", "false");
    if (user) {
      try {
        await api.updateSettings({ has_seen_onboarding: false });
      } catch (e) {
        // ignore
      }
    }
  }, [user]);

  const t = useCallback(
    (key) => {
      const dict = translations[language] || translations.en;
      return dict[key] || translations.en[key] || key;
    },
    [language]
  );

  return (
    <SettingsContext.Provider
      value={{
        dyslexiaMode,
        language,
        hasSeenOnboarding,
        loaded,
        setDyslexiaMode: updateDyslexia,
        setLanguage: updateLanguage,
        markOnboardingSeen,
        resetOnboarding,
        t,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within SettingsProvider");
  return ctx;
};
